'use client'

import React, { useMemo, useState } from 'react'

import { Media } from '@/theme/components/Media/Media'
import type { GalleryBlock as GalleryBlockProps } from '@/payload-types'

const CATEGORY_LABELS: Record<string, string> = {
  long: 'Long',
  medium: 'Medium',
  court: 'Court',
  extensions: 'Extensions',
  homme: 'Homme',
  enfant: 'Enfant',
}

export const GalleryBlockComponent: React.FC<GalleryBlockProps> = ({ title, subtitle, images }) => {
  const categories = useMemo(() => {
    const present = new Set<string>()
    images?.forEach((img) => img.category && present.add(img.category))
    return Object.keys(CATEGORY_LABELS).filter((key) => present.has(key))
  }, [images])

  // Pas d'onglet "Tout voir" : avec 46 photos au total, tout afficher d'un
  // coup produirait un mur illisible - on atterrit toujours sur la première
  // catégorie, comme le fait le site d'origine.
  const [activeCategory, setActiveCategory] = useState<string | null>(() => categories[0] ?? null)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const filteredImages = useMemo(() => {
    if (!images) return []
    if (!activeCategory) return images
    return images.filter((img) => img.category === activeCategory)
  }, [images, activeCategory])

  const hasImages = images?.some((img) => img.image && typeof img.image === 'object')
  if (!hasImages) return null

  const openLightbox = (index: number) => setLightboxIndex(index)
  const closeLightbox = () => setLightboxIndex(null)
  const showRelative = (delta: number) => {
    if (lightboxIndex === null || filteredImages.length === 0) return
    setLightboxIndex((lightboxIndex + delta + filteredImages.length) % filteredImages.length)
  }

  const activeImage =
    lightboxIndex !== null && filteredImages[lightboxIndex]?.image && typeof filteredImages[lightboxIndex]?.image === 'object'
      ? filteredImages[lightboxIndex]
      : null

  return (
    <section id="galerie">
      <div className="section-inner container">
        <div className="section-head centered">
          <p className="eyebrow">Galerie</p>
          {title && <h2>{title}</h2>}
          {subtitle && <p className="mt-3 mx-auto">{subtitle}</p>}
        </div>

        {categories.length > 1 && (
          <div className="gallery-filters">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                className={`gallery-filter${activeCategory === cat ? ' active' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                {CATEGORY_LABELS[cat] ?? cat}
              </button>
            ))}
          </div>
        )}

        <div className="gallery-grid">
          {filteredImages.map((item, index) => {
            if (!item.image || typeof item.image !== 'object') return null
            return (
              <button
                key={item.id ?? index}
                type="button"
                className="gallery-item"
                onClick={() => openLightbox(index)}
                aria-label={item.caption ? `Agrandir : ${item.caption}` : 'Agrandir la photo'}
              >
                <Media resource={item.image} fill imgClassName="gallery-item-img" htmlElement={null} />
                {(item.caption || item.category) && (
                  <span className="gallery-item-overlay">
                    {item.category && (
                      <span className="gallery-item-tag">{CATEGORY_LABELS[item.category] ?? item.category}</span>
                    )}
                    {item.caption && <span className="gallery-item-caption">{item.caption}</span>}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {activeImage?.image && typeof activeImage.image === 'object' && (
        <div className="lightbox" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox} aria-label="Fermer">
            ✕
          </button>
          <button
            className="lightbox-nav prev"
            aria-label="Image précédente"
            onClick={(e) => {
              e.stopPropagation()
              showRelative(-1)
            }}
          >
            ‹
          </button>
          <div className="lightbox-media" onClick={(e) => e.stopPropagation()}>
            <Media
              resource={activeImage.image}
              imgClassName="max-w-[90vw] max-h-[78vh] object-contain"
            />
            <div className="lightbox-caption">
              {activeImage.caption && <span>{activeImage.caption}</span>}
              <span className="lightbox-counter">
                {(lightboxIndex ?? 0) + 1} / {filteredImages.length}
              </span>
            </div>
          </div>
          <button
            className="lightbox-nav next"
            aria-label="Image suivante"
            onClick={(e) => {
              e.stopPropagation()
              showRelative(1)
            }}
          >
            ›
          </button>
        </div>
      )}
    </section>
  )
}
